import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  GetTemplateDependenciesQueryKey,
  GetTemplateVersionsQueryKey,
  useApiPublishTemplateVersionMutation,
} from '@seeqdev/api/endpoint/TemplatesApi';
import { useTemplateLibraryQuery } from '@seeqdev/api/graphql/TemplateLibrary';

/**
 * Invalidates the cached version list for a template container. Exported as a primitive for callers
 * that already hold a QueryClient; React components should prefer `usePublishTemplateVersionMutation`
 * so invalidation happens automatically as part of the publish flow.
 *
 * Every subscriber to the `GetTemplateVersionsQueryKey` must use the same key construction or
 * invalidation silently misses.
 */
export function invalidateTemplateVersions(queryClient: QueryClient, templateId: string) {
  return queryClient.invalidateQueries({
    queryKey: GetTemplateVersionsQueryKey({ templateId: templateId.toLowerCase() }),
  });
}

/**
 * Invalidates the cached dependency graph so changes are picked up after publishing.
 */
export function invalidateTemplateDependencies(queryClient: QueryClient, templateId: string) {
  return queryClient.invalidateQueries({
    queryKey: GetTemplateDependenciesQueryKey({ templateId: templateId.toLowerCase() }),
  });
}

/**
 * Invalidates every Template Library list (Mine, Shared and Industry, for every workbook) so consumers refetch after
 * any mutation to a template row. Every list is hit because a mutated template can appear in more than one: the user
 * can hold write access on a shared template they don't own, and a global template appears in every workbook's lists.
 */
export function invalidateTemplateLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: useTemplateLibraryQuery.getKey() });
}

/**
 * Canonical hook for publishing a template version. Thin wrapper around the generated typed
 * `useApiPublishTemplateVersionMutation` that adds an `onSuccess` step to invalidate the
 * `GetTemplateVersionsQueryKey` and `GetTemplateDependenciesQueryKey` for the affected templateId
 * plus the Template Library lists, so consumers of version metadata (e.g. the scaling-table
 * `TemplateStaleBanner`), the dependency graph (output/input selection), and the template library
 * re-derive without a page reload. Every publish path in the app should route through this hook.
 *
 * Mutation params follow the SDK shape: `[body, urlParams]`, i.e. `[{ versionId }, { templateId }]`.
 */
export const usePublishTemplateVersionMutation = () => {
  const queryClient = useQueryClient();
  return useApiPublishTemplateVersionMutation({
    onSuccess: (_data, [, { templateId }]) => {
      void invalidateTemplateVersions(queryClient, templateId);
      void invalidateTemplateDependencies(queryClient, templateId);
      invalidateTemplateLists(queryClient);
    },
  });
};
